
	// Vue
	import { Component, Vue, Prop, Emit } from "vue-property-decorator";
	import { namespace, Getter } from "vuex-class";
	import { getLandingPages, RouteConfigX, findRouteByKey, routeHasPermission } from "@/router";

	// Utils
	import "@/scripts/dictionary";
	import "@/scripts/array-operations";
	import { validationMixin } from "vuelidate";

	// Types
	import { UserRole } from "@/store/user-management/types";
	import { SiteTreeNode } from "@/store/eventqueue/types";
	import { UserPermissions, FeaturesList } from '@/store/types';

	// Components
	import TreeselectNode from "@/types/area-tree/TreeselectNode";
	import vSelect3 from "vselect3";
	import VueTreeselect from "@riophae/vue-treeselect";

	// Vuex namespaces
	const UserManagement = namespace("userManagement");
	const EventQueue = namespace("eventqueue");

	export interface UserGroupRole {
		groupId: number | null;
		userRoleId: number | null;
		inherit: boolean;
		groupTitle: string;
		userRoleTitle: string;
		landingPage: string;
	}

	@Component({
		mixins: [validationMixin],
		validations: {
			userGroupRoles: {
				$each: {
					landingPage: {
						isValidRoute: function(_, ugr: UserGroupRole) {
							return routeHasPermission(
								findRouteByKey(ugr.landingPage),
								this.userPermissionsForRole(ugr.userRoleId)
							);
						},
					},
					groupId: {},
					userRoleId: {},
					inherit: {},
				},
			},
		},
		components: {
			"v-select3": vSelect3,
			"vue-treeselect": VueTreeselect
		}
	})
	export default class UserGroupRoles extends Vue {
		@Prop({ type: Array, default: () => [] })
		public value: UserGroupRole[];

		@Prop()
		public selectedTenantGroupId: number;

		@EventQueue.Getter private getSiteTreeForSelectedTenant: (selectedTenantId: number) => SiteTreeNode[];

		@UserManagement.Getter("getUserRoles") private userRoles: UserRole[];

		@Getter("getFeaturesList") featuresList: FeaturesList;

		@Getter getFeature: (featureName: string[]) => boolean;

		private userGroupRoles: UserGroupRole[] = [];

		/**
		 * Returns the list of sites, with any branches of the tree that the user already has
		 * permissions assigned for disabled.
		 */
		private get availableSites(): TreeselectNode[] {
			const convertNode = (node: SiteTreeNode, disabled: boolean = false): TreeselectNode => {
				const associatedRole = this.value.find(role => role.groupId == node.group.groupID);

				const children =
					node.subGroups.length > 0
						? node.subGroups.map(n => convertNode(n, associatedRole?.inherit ?? false))
						: [];

				return {
					id: node.group.groupID,
					label: node.group.title,
					children: children.length > 0 ? children : undefined,
					isDefaultExpanded: node.isOpenDefault,
					isDisabled: associatedRole != null || disabled
				};
			};

			return this.getSiteTreeForSelectedTenant(this.selectedTenantGroupId).map(n => convertNode(n));
		}

		/**
		 * Updates the current list of user roles with the values passed in by the generic table.
		 */
		public mounted() {
			this.value.forEach(userGroupRole => {
				this.userGroupRoles.push(userGroupRole);
			});
		}

		/**
		 * Adds a new empty user role to the list.
		 */
		private addNewRole() {
			const newUserGroup: UserGroupRole = {
				groupId: null,
				userRoleId: null,
				inherit: true,
				groupTitle: "",
				userRoleTitle: "",
				landingPage: "",
			};

			this.value.push(newUserGroup);
			this.userGroupRoles.push(newUserGroup);
			this.$emit("input", this.userGroupRoles);
		}

		/**
		 * Removes a specified user role from the list.
		 */
		private removeRole(roleToRemove: UserGroupRole) {
			this.value.remove(roleToRemove);
			this.userGroupRoles.remove(roleToRemove);
			this.$emit("input", this.userGroupRoles);
		}

		@Emit("change")
		private handleDataChanged(ugr: UserGroupRole) {
			if (!ugr) return;

			const result = routeHasPermission(findRouteByKey(ugr.landingPage), this.userPermissionsForRole(ugr.userRoleId));
			if (!result) {
				ugr.landingPage = "";
				this.$emit("change");
			}
		}

		private userPermissionsForRole(userRoleId: number): UserPermissions {
			const role = this.userRoles.find(x => x.userRoleId == userRoleId);

			const permissions = role?.permissions?.map(x => x.permission) ?? [];

			const permissionKeys = {} as UserPermissions;

			if (permissions) {
				permissions.forEach(x => {
					// Sets permission title as keys
					// Lower case first letter of the permission title for the key
					permissionKeys[x.title.charAt(0).toLowerCase() + x.title.slice(1)] = true;
				});
			}
			return permissionKeys;
		}

		private landingPages(userRoleId: number): RouteConfigX[] {
			if (!userRoleId) return [];

            return getLandingPages(true, this.userPermissionsForRole(userRoleId));
		}

		private reduceRoute(option: RouteConfigX | string) {
			if (!option) return "";
			if (typeof option == "string") {
				const route = findRouteByKey(option);
				return route.meta.title ?? "";
			}

			return option.meta.title ?? "";
		}

		private get landingPageFeature() {
			return this.getFeature(['Users', 'LandingPage']);
		}
	}
